import { keyframes } from '@emotion/react';
import {
  alpha,
  Box,
  styled,
  Theme,
  Typography,
  TypographyProps,
} from '@mui/material';
import { ImgHTMLAttributes } from 'react';

import { ProgressDots } from '@/components/DataDisplay/Indicator/ProgressDots';
import { OverlayScroller } from '@/components/Layout/OverlayScroller';
import { ForegroundImageProps } from '@/components/Layout/Page/Page.interface';

import { getAssetPath } from '@/constants/configs';

import { doNotForwardTheseProps, elevationLevel } from '@/styles';

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const scale = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(0.7); }
`;

export const PendingProfileContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  right: '22%',
  transform: 'translate(0, -50%)',
  background: theme.palette.common.white,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
  padding: theme.spacing(4, 6),
  zIndex: 2,
  minWidth: 552,
  maxWidth: 552,
  width: '100%',
  height: 360,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    position: 'static',
    top: 'auto',
    right: 'auto',
    transform: 'none',
    background: 'none',
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'none',
    zIndex: 'auto',
    minWidth: 'unset',
    maxWidth: 'unset',
    width: '80%',
    height: 98,
    marginTop: theme.spacing(10),
    padding: theme.spacing(2, 1.5),
    justifyContent: 'space-between',
    justifySelf: 'center',
    paddingBottom: theme.spacing(10),
  },
}));

export const PageBackground = styled(Box, {
  shouldForwardProp: doNotForwardTheseProps('isDark', 'bgColor'),
})<{ isDark?: boolean; bgColor?: string }>(({ theme, isDark, bgColor }) => ({
  backgroundColor:
    bgColor ||
    (isDark ? theme.palette.neutral[900] : theme.palette.background.paper),
  color: isDark ? theme.palette.common.white : 'initial',
  height: '100vh',
  width: '100vw',
  overflowY: 'auto',
  [theme.breakpoints.up('lg')]: {
    position: 'fixed',
  },
}));

export const PageContent = styled(OverlayScroller)(({ theme }) => ({
  position: 'relative',
  '.simplebar-content': {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    overflowY: 'auto',
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.up('lg')]: {
      paddingBottom: 0,
    },
  },
}));

export const PageFormContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100dvh',
  paddingTop: theme.layout.headerHeight.xs,
  paddingBottom: theme.spacing(6),
  width: '100dvw',
  alignItems: 'center',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  gap: theme.spacing(1),
  marginLeft: 'auto',
  marginRight: 'auto',
  maxWidth: 401,
  [theme.breakpoints.up('lg')]: {
    gap: theme.spacing(3),
    transform: `translate(calc(-50% + ${theme.spacing(-3)}), 0)`,
    width: 552,
    height: '100%',
    maxHeight: `min(100vh, 774px)`,
    maxWidth: 'unset',
    margin: 0,
    padding: `${theme.spacing(4)} 0 0 0`,
    alignItems: 'flex-start',
    overflowY: 'auto',
  },
}));

export const PageProgressDots = styled(ProgressDots)(({ theme }) => ({
  alignSelf: 'flex-start',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
}));

export const PageLogo = styled((props: ImgHTMLAttributes<HTMLImageElement>) => (
  <img src={getAssetPath('/logo-with-text.svg')} alt="page-logo" {...props} />
))(({ theme }) => ({
  width: 206,
  margin: `${theme.spacing(6)} auto ${theme.spacing(8)}`,
  [theme.breakpoints.up('lg')]: {
    width: 144,
    margin: 0,
    marginBottom: theme.spacing(1),
  },
}));

export const PageTitleContent = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  fontSize: theme.typography.h3.fontSize,
  fontWeight: theme.typography.h3.fontWeight,
  lineHeight: theme.typography.h3.lineHeight,
  [theme.breakpoints.up('lg')]: {
    textAlign: 'left',
    fontSize: theme.typography.h2.fontSize,
    fontWeight: theme.typography.h2.fontWeight,
    lineHeight: theme.typography.h2.lineHeight,
  },
}));

export const StyledProgress = styled(ProgressDots)(({ theme }) => ({
  alignSelf: 'flex-start',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
}));

export const BackgroundPaperContent = styled(Box, {
  shouldForwardProp: doNotForwardTheseProps('childCount'),
})<{ childCount?: number }>(({ theme, childCount = 0 }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: childCount > 1 ? 'space-between' : 'center',
  color: theme.palette.common.white,
  height: '100%',
  width: '100%',
  maxWidth: 648,
  maxHeight: 740,
}));

export const PageNavBar = styled(Box, {
  shouldForwardProp: doNotForwardTheseProps('isDarkBg'),
})<{ isDarkBg?: boolean }>(({ theme, isDarkBg }) => ({
  flex: 1,
  alignItems: 'flex-start',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  '.MuiButtonBase-root': {
    color: isDarkBg ? theme.palette.common.white : 'initial',
  },
}));

const getBackgroundPaperStylesBySize = (
  size: 'small' | 'medium' | 'big' | 'bigger',
  theme: Theme,
) => {
  switch (size) {
    case 'medium':
      return {
        width: '36%',
      };
    case 'big':
      return {
        padding: theme.spacing(12),
        width: '50%',
      };
    case 'bigger':
      return {
        padding: theme.spacing(12),
        width: '62.5%',
      };
    default:
      return {
        width: '16%',
      };
  }
};

export const StyledBackgroundPaper = styled('div', {
  shouldForwardProp: doNotForwardTheseProps(
    'colorFrom',
    'colorTo',
    'position',
    'size',
    'image',
    'hasShadow',
    'hasGradient',
  ),
})<{
  colorFrom: string;
  colorTo: string;
  position: 'left' | 'right';
  size: 'small' | 'medium' | 'big' | 'bigger';
  image?: string;
  hasShadow?: boolean;
  hasGradient?: boolean;
}>(
  ({
    theme,
    colorFrom,
    colorTo,
    position,
    size,
    image,
    hasShadow,
    hasGradient,
  }) => ({
    position: 'fixed',
    height: '100vh',
    top: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    boxShadow: hasShadow ? theme.shadows[elevationLevel.l] : 'none',
    zIndex: 1,
    ...(image
      ? {
          ...(hasGradient
            ? {
                backgroundImage: `linear-gradient(${position === 'left' ? 'to right' : 'to left'}, ${alpha(colorFrom, 0.5)}, ${alpha(colorTo, 0.5)}), url(${image})`,
              }
            : {
                backgroundImage: `url(${image})`,
              }),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : {
          background: `linear-gradient(${position === 'left' ? 'to right' : 'to left'}, ${colorFrom}, ${colorTo})`,
        }),
    ...(position === 'left'
      ? {
          left: 0,
          borderTopRightRadius: 64,
          borderBottomRightRadius: 64,
          alignItems: 'flex-end',
        }
      : {
          right: 0,
          borderTopLeftRadius: 64,
          borderBottomLeftRadius: 64,
          alignItems: 'flex-start',
        }),
    ...getBackgroundPaperStylesBySize(size, theme),
  }),
);

export const ForegroundImage = styled('img', {
  shouldForwardProp: doNotForwardTheseProps(
    'position',
    'size',
    'overflowPercent',
    'width',
  ),
})<ForegroundImageProps>(({ position, size, overflowPercent, width }) => ({
  position: 'absolute',
  top: '50%',
  ...(size === 'big' || size === 'bigger'
    ? {
        width: '100%',
      }
    : {
        width: width || 280,
      }),
  ...(position === 'left'
    ? {
        right: 0,
        transform: `translate(${overflowPercent || 34}%, -50%)`,
      }
    : {
        left: 0,
        transform: `translate(-${overflowPercent || 34}%, -50%)`,
      }),
}));

export const BgPaperLogoImage = styled(
  (props: ImgHTMLAttributes<HTMLImageElement>) => (
    <img
      src={getAssetPath('/logo-with-text-white.svg')}
      alt="Logo"
      {...props}
    />
  ),
)(() => ({
  width: 174,
}));

export const ScrollingIcons = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: 240,
  overflow: 'hidden',
  position: 'relative',
});

export const IconsTrack = styled(Box)`
  display: flex;
  align-items: center;
  animation: ${scroll} 3s linear infinite;
  & > * {
    margin: 0 8px;
    animation: ${scale} 3s linear infinite;
  }
`;

export const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  flex: 1,
  [theme.breakpoints.up('lg')]: {
    maxWidth: 400,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    textAlign: 'left',
    flex: 0,
  },
}));

export const ResponsiveMarginTopMessage = styled((props: TypographyProps) => (
  <Typography variant="headlineH2" {...props} />
))(({ theme }) => ({
  marginTop: theme.spacing(4),
  [theme.breakpoints.up('lg')]: {
    marginTop: theme.spacing(20),
  },
}));
